import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { marketing } from '../services/api';
import { toast } from '../hooks/use-toast';
import { Users, Loader2, Download, Trash2, Search, Filter } from 'lucide-react';
import { MarketingLocked } from './LandingPageBuilder';

// Contact list (List Builder feature #3). Shows opt-ins + survey responses.
// One-click Excel export via a signed URL.
export default function OwnerContacts() {
  const [rows, setRows] = useState(null);
  const [locked, setLocked] = useState(false);
  const [q, setQ] = useState('');
  const [source, setSource] = useState('');

  const load = async () => {
    try {
      const r = await marketing.contacts({ source });
      setRows(r);
    } catch (e) {
      if (e?.response?.status === 403) setLocked(true);
      else toast({ title: 'Failed to load contacts' });
      setRows([]);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [source]);

  const remove = async (c) => {
    if (!window.confirm(`Remove ${c.email}? This won't unsubscribe them elsewhere.`)) return;
    try { await marketing.deleteContact(c.id); setRows(prev => prev.filter(r => r.id !== c.id)); toast({ title: 'Contact removed' }); }
    catch { toast({ title: 'Remove failed' }); }
  };

  if (locked) return <MarketingLocked/>;
  if (rows === null) return <div className="text-gray-400 inline-flex items-center gap-2"><Loader2 size={14} className="animate-spin"/> Loading…</div>;

  const term = q.trim().toLowerCase();
  const visible = term
    ? rows.filter(r => (r.email || '').toLowerCase().includes(term) || (r.name || '').toLowerCase().includes(term))
    : rows;

  return (
    <div className="space-y-4" data-testid="owner-contacts-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold inline-flex items-center gap-2"><Users size={22} className="text-green-400"/> My contact list</h1>
          <p className="text-gray-400 text-sm">Opt-ins from your landing page and survey responses live here.</p>
        </div>
        <a href={marketing.exportContactsUrl()} target="_blank" rel="noreferrer"
          data-testid="contacts-export-xlsx"
          className="text-xs px-3 py-1.5 rounded-lg bg-green-600 hover:bg-green-500 text-white inline-flex items-center gap-1">
          <Download size={12}/> Export to Excel
        </a>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative">
          <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-500"/>
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search email or name"
            data-testid="contacts-search"
            className="pl-7 pr-3 py-1.5 rounded-lg text-sm bg-black border border-[#2a2a2a] text-white placeholder-gray-500 focus:outline-none focus:border-green-500"/>
        </div>
        <select value={source} onChange={e => setSource(e.target.value)}
          data-testid="contacts-source-filter"
          className="text-sm bg-black border border-[#2a2a2a] rounded-lg px-2 py-1.5 text-gray-200">
          <option value="">All sources</option>
          <option value="landing_page">Landing page opt-in</option>
          <option value="survey">Survey</option>
          <option value="manual">Manual add</option>
        </select>
        <div className="text-xs text-gray-500 inline-flex items-center gap-1"><Filter size={11}/> {visible.length} of {rows.length}</div>
      </div>

      {visible.length === 0 ? (
        <div className="pbb-card p-10 text-center" data-testid="contacts-empty">
          <Users className="mx-auto text-gray-500" size={26}/>
          <div className="mt-2 font-semibold text-white">No contacts yet</div>
          <p className="text-sm text-gray-400 mt-1">Share your landing page — every opt-in shows up here.</p>
          <Link to="/marketing/landing" className="pbb-btn-green mt-4 inline-flex max-w-xs">Open Landing Page Builder</Link>
        </div>
      ) : (
        <div className="pbb-card divide-y divide-[#1a1a1a]" data-testid="contacts-list">
          {visible.map(c => (
            <div key={c.id} className="flex items-center gap-3 p-3" data-testid={`contact-row-${c.id}`}>
              <div className="flex-1 min-w-0">
                <div className="text-white text-sm truncate">{c.name || '(unnamed)'} <span className="text-gray-500">&middot;</span> <span className="text-gray-300">{c.email}</span></div>
                <div className="text-[11px] text-gray-500">{c.source} · {new Date(c.createdAt).toLocaleDateString()}{c.phone ? ` · ${c.phone}` : ''}</div>
              </div>
              <button onClick={() => remove(c)} data-testid={`contact-delete-${c.id}`}
                className="text-xs px-2 py-1 rounded border border-[#2a2a2a] hover:border-red-500 text-gray-400 hover:text-red-400 inline-flex items-center gap-1">
                <Trash2 size={11}/> Remove
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
